import { generateId } from './measurements';

/**
 * Detect enclosed rectangular rooms from a set of walls.
 *
 * Algorithm:
 * 1. Build a graph of wall endpoints (vertices)
 * 2. Find all unique vertices where walls meet
 * 3. For each potential rectangle (4 vertices), check if all 4 edges exist as walls
 * 4. Create room objects with wallIds for valid rectangles
 */

const EPSILON = 2; // Tolerance for coordinate matching

/**
 * Check if two points are approximately equal
 */
const pointsEqual = (p1, p2) => {
  return Math.abs(p1.x - p2.x) < EPSILON && Math.abs(p1.y - p2.y) < EPSILON;
};

/**
 * Create a normalized key for a point (for deduplication)
 */
const pointKey = (p) => {
  const x = Math.round(p.x / EPSILON) * EPSILON;
  const y = Math.round(p.y / EPSILON) * EPSILON;
  return `${x},${y}`;
};

/**
 * Create a normalized key for a wall segment (for lookup)
 */
const wallSegmentKey = (p1, p2) => {
  const k1 = pointKey(p1);
  const k2 = pointKey(p2);
  return k1 < k2 ? `${k1}-${k2}` : `${k2}-${k1}`;
};

/**
 * Check if a wall is horizontal
 */
const isHorizontal = (wall) => Math.abs(wall.start.y - wall.end.y) < EPSILON;

/**
 * Check if a wall is vertical
 */
const isVertical = (wall) => Math.abs(wall.start.x - wall.end.x) < EPSILON;

/**
 * Get room color based on area (larger rooms get different default colors)
 */
const getDefaultRoomColor = (area, index) => {
  const colors = [
    'rgba(100, 200, 255, 0.15)', // Light blue
    'rgba(200, 150, 255, 0.15)', // Light purple
    'rgba(255, 200, 100, 0.15)', // Light orange
    'rgba(100, 255, 200, 0.15)', // Light teal
    'rgba(255, 150, 150, 0.15)', // Light red
    'rgba(150, 200, 150, 0.15)', // Light green
    'rgba(180, 180, 220, 0.12)', // Light lavender
    'rgba(200, 200, 200, 0.12)', // Light gray
  ];
  return colors[index % colors.length];
};

/**
 * Find a wall that connects two points (or a segment of a wall)
 * Returns the wall ID if found, null otherwise
 */
const findWallBetweenPoints = (p1, p2, walls, wallSegmentMap) => {
  const segKey = wallSegmentKey(p1, p2);

  // First check exact matches
  if (wallSegmentMap.has(segKey)) {
    return wallSegmentMap.get(segKey);
  }

  // Check if this segment is part of a longer wall
  for (const wall of walls) {
    const isHoriz = isHorizontal(wall);
    const isVert = isVertical(wall);

    if (isHoriz && Math.abs(p1.y - wall.start.y) < EPSILON && Math.abs(p2.y - wall.start.y) < EPSILON) {
      // Both points on same horizontal line as wall
      const wallMinX = Math.min(wall.start.x, wall.end.x);
      const wallMaxX = Math.max(wall.start.x, wall.end.x);
      const segMinX = Math.min(p1.x, p2.x);
      const segMaxX = Math.max(p1.x, p2.x);

      if (segMinX >= wallMinX - EPSILON && segMaxX <= wallMaxX + EPSILON) {
        return wall.id;
      }
    }

    if (isVert && Math.abs(p1.x - wall.start.x) < EPSILON && Math.abs(p2.x - wall.start.x) < EPSILON) {
      // Both points on same vertical line as wall
      const wallMinY = Math.min(wall.start.y, wall.end.y);
      const wallMaxY = Math.max(wall.start.y, wall.end.y);
      const segMinY = Math.min(p1.y, p2.y);
      const segMaxY = Math.max(p1.y, p2.y);

      if (segMinY >= wallMinY - EPSILON && segMaxY <= wallMaxY + EPSILON) {
        return wall.id;
      }
    }
  }

  return null;
};

/**
 * Axis-aligned bounding box of a room's points
 */
const roomBoundingBox = (room) => room.points.reduce((acc, p) => ({
  minX: Math.min(acc.minX, p.x),
  maxX: Math.max(acc.maxX, p.x),
  minY: Math.min(acc.minY, p.y),
  maxY: Math.max(acc.maxY, p.y),
}), { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });

/**
 * Check if a rectangle overlaps the floor area of any of the given rooms.
 *
 * Rooms tile the plan - they may share a wall, but they may never share floor.
 * So this measures actual overlapping AREA rather than comparing centers: a
 * rectangle spanning two rooms, or the whole building envelope, has a different
 * center from every room inside it and would otherwise pass as distinct. Shared
 * edges produce zero overlap and are allowed through.
 */
const rectangleOverlapsRoom = (rect, existingRooms) => {
  for (const room of existingRooms) {
    if (!room.points || room.points.length < 3) continue;

    const bounds = roomBoundingBox(room);
    const overlapX = Math.min(rect.maxX, bounds.maxX) - Math.max(rect.minX, bounds.minX);
    const overlapY = Math.min(rect.maxY, bounds.maxY) - Math.max(rect.minY, bounds.minY);

    if (overlapX > EPSILON && overlapY > EPSILON) {
      return true;
    }
  }

  return false;
};

/**
 * Detect rooms from walls
 *
 * @param {Array} walls - Array of wall objects with {id, start: {x, y}, end: {x, y}}
 * @param {Array} existingRooms - Array of existing room objects to avoid duplicates
 * @returns {Array} - Array of detected room objects
 */
export const detectRoomsFromWalls = (walls, existingRooms = []) => {
  if (!walls || walls.length < 4) return [];

  // Filter to only axis-aligned walls (horizontal or vertical)
  const axisAlignedWalls = walls.filter(w => isHorizontal(w) || isVertical(w));

  if (axisAlignedWalls.length < 4) return [];

  // Build a map of wall segments for quick lookup
  const wallSegmentMap = new Map();
  for (const wall of axisAlignedWalls) {
    const key = wallSegmentKey(wall.start, wall.end);
    wallSegmentMap.set(key, wall.id);
  }

  // Collect all unique vertices (wall endpoints and intersections)
  const vertexMap = new Map();

  for (const wall of axisAlignedWalls) {
    const startKey = pointKey(wall.start);
    const endKey = pointKey(wall.end);

    if (!vertexMap.has(startKey)) {
      vertexMap.set(startKey, { x: wall.start.x, y: wall.start.y });
    }
    if (!vertexMap.has(endKey)) {
      vertexMap.set(endKey, { x: wall.end.x, y: wall.end.y });
    }
  }

  // Also find intersections between walls
  for (let i = 0; i < axisAlignedWalls.length; i++) {
    for (let j = i + 1; j < axisAlignedWalls.length; j++) {
      const w1 = axisAlignedWalls[i];
      const w2 = axisAlignedWalls[j];

      // Check for T-intersections or crossings
      if (isHorizontal(w1) && isVertical(w2)) {
        const hMinX = Math.min(w1.start.x, w1.end.x);
        const hMaxX = Math.max(w1.start.x, w1.end.x);
        const vMinY = Math.min(w2.start.y, w2.end.y);
        const vMaxY = Math.max(w2.start.y, w2.end.y);

        if (w2.start.x >= hMinX - EPSILON && w2.start.x <= hMaxX + EPSILON &&
            w1.start.y >= vMinY - EPSILON && w1.start.y <= vMaxY + EPSILON) {
          const intersect = { x: w2.start.x, y: w1.start.y };
          const key = pointKey(intersect);
          if (!vertexMap.has(key)) {
            vertexMap.set(key, intersect);
          }
        }
      } else if (isVertical(w1) && isHorizontal(w2)) {
        const hMinX = Math.min(w2.start.x, w2.end.x);
        const hMaxX = Math.max(w2.start.x, w2.end.x);
        const vMinY = Math.min(w1.start.y, w1.end.y);
        const vMaxY = Math.max(w1.start.y, w1.end.y);

        if (w1.start.x >= hMinX - EPSILON && w1.start.x <= hMaxX + EPSILON &&
            w2.start.y >= vMinY - EPSILON && w2.start.y <= vMaxY + EPSILON) {
          const intersect = { x: w1.start.x, y: w2.start.y };
          const key = pointKey(intersect);
          if (!vertexMap.has(key)) {
            vertexMap.set(key, intersect);
          }
        }
      }
    }
  }

  const vertices = Array.from(vertexMap.values());

  // Get unique X and Y coordinates
  const xCoords = [...new Set(vertices.map(v => Math.round(v.x / EPSILON) * EPSILON))].sort((a, b) => a - b);
  const yCoords = [...new Set(vertices.map(v => Math.round(v.y / EPSILON) * EPSILON))].sort((a, b) => a - b);

  // Every wall-bounded rectangle is a candidate, including ones that contain others:
  // a rectangle around two bedrooms is as "valid" as either bedroom. They are all
  // collected first and the real rooms picked out afterwards, smallest first.
  const candidates = [];
  const usedRectangles = new Set();

  // Try to find rectangles by checking all combinations of x and y coordinates
  for (let xi1 = 0; xi1 < xCoords.length; xi1++) {
    for (let xi2 = xi1 + 1; xi2 < xCoords.length; xi2++) {
      for (let yi1 = 0; yi1 < yCoords.length; yi1++) {
        for (let yi2 = yi1 + 1; yi2 < yCoords.length; yi2++) {
          const minX = xCoords[xi1];
          const maxX = xCoords[xi2];
          const minY = yCoords[yi1];
          const maxY = yCoords[yi2];

          // Skip very small rectangles (less than ~2 feet on a side)
          if (maxX - minX < 60 || maxY - minY < 60) continue;

          const rectKey = `${minX},${minY}-${maxX},${maxY}`;
          if (usedRectangles.has(rectKey)) continue;

          // Define the 4 corners
          const topLeft = { x: minX, y: minY };
          const topRight = { x: maxX, y: minY };
          const bottomRight = { x: maxX, y: maxY };
          const bottomLeft = { x: minX, y: maxY };

          // Check if all 4 edges have walls
          const topWallId = findWallBetweenPoints(topLeft, topRight, axisAlignedWalls, wallSegmentMap);
          const rightWallId = findWallBetweenPoints(topRight, bottomRight, axisAlignedWalls, wallSegmentMap);
          const bottomWallId = findWallBetweenPoints(bottomRight, bottomLeft, axisAlignedWalls, wallSegmentMap);
          const leftWallId = findWallBetweenPoints(bottomLeft, topLeft, axisAlignedWalls, wallSegmentMap);

          if (topWallId && rightWallId && bottomWallId && leftWallId) {
            usedRectangles.add(rectKey);

            candidates.push({
              minX, maxX, minY, maxY,
              area: (maxX - minX) * (maxY - minY),
              wallIds: [topWallId, rightWallId, bottomWallId, leftWallId],
            });
          }
        }
      }
    }
  }

  // A room is the SMALLEST wall-bounded rectangle covering its floor - the space you
  // stand in, not that space plus its neighbours. So take candidates smallest first
  // and keep one only while its floor is still unclaimed. Larger rectangles then fall
  // out naturally: they overlap the rooms they are made of. Sorting largest first
  // instead would keep the building envelope and discard every room in it.
  candidates.sort((a, b) => a.area - b.area);

  const detectedRooms = [];
  for (const rect of candidates) {
    if (rectangleOverlapsRoom(rect, existingRooms)) continue;
    if (rectangleOverlapsRoom(rect, detectedRooms)) continue;

    detectedRooms.push({
      id: generateId(),
      name: `Room ${detectedRooms.length + 1}`,
      color: getDefaultRoomColor(rect.area, detectedRooms.length),
      wallIds: rect.wallIds,
      points: [
        { x: rect.minX, y: rect.minY },
        { x: rect.maxX, y: rect.minY },
        { x: rect.maxX, y: rect.maxY },
        { x: rect.minX, y: rect.maxY },
      ],
    });
  }

  return detectedRooms;
};

/**
 * Update room wallIds based on current wall positions
 * Useful after walls have been moved
 */
export const updateRoomWallIds = (room, walls) => {
  if (!room.points || room.points.length !== 4) return room;

  const edges = [
    { p1: room.points[0], p2: room.points[1] }, // Top
    { p1: room.points[1], p2: room.points[2] }, // Right
    { p1: room.points[2], p2: room.points[3] }, // Bottom
    { p1: room.points[3], p2: room.points[0] }, // Left
  ];

  const wallSegmentMap = new Map();
  for (const wall of walls) {
    const key = wallSegmentKey(wall.start, wall.end);
    wallSegmentMap.set(key, wall.id);
  }

  const newWallIds = edges.map(edge =>
    findWallBetweenPoints(edge.p1, edge.p2, walls, wallSegmentMap)
  ).filter(id => id !== null);

  return {
    ...room,
    wallIds: newWallIds,
  };
};
